/**
 * Spawn-position search helpers.
 * Ported from supersplat-viewer/src/collision/find-spawn.ts.
 */

import type { Collision } from './collision';

const SEARCH_RADIUS = 5;
const SEARCH_RADIUS_SQ = SEARCH_RADIUS * SEARCH_RADIUS;
const RAY_MAX_DIST = 1000;

interface SpawnOut {
    x: number;
    y: number;
    z: number;
}

const scratchPush = { x: 0, y: 0, z: 0 };

/** Find the closest sphere placement to (ox,oy,oz) where a sphere of `radius` fits clear. */
const findSphereSpawn = (
    collision: Collision,
    ox: number, oy: number, oz: number,
    radius: number,
    out: SpawnOut
): boolean => {
    const step = collision.voxelResolution;
    const maxCells = Math.ceil(SEARCH_RADIUS / step);
    let bestDistSq = Infinity;
    let found = false;

    for (let r = 0; r <= maxCells; r++) {
        const shellMinDistSq = (r * step) * (r * step);
        if (shellMinDistSq >= bestDistSq) break;
        for (let dy = -r; dy <= r; dy++) {
            const absDy = dy < 0 ? -dy : dy;
            for (let dz = -r; dz <= r; dz++) {
                const absDz = dz < 0 ? -dz : dz;
                for (let dx = -r; dx <= r; dx++) {
                    const absDx = dx < 0 ? -dx : dx;
                    if (absDx < r && absDy < r && absDz < r) continue;
                    const distSq = (dx * dx + dy * dy + dz * dz) * step * step;
                    if (distSq >= bestDistSq || distSq > SEARCH_RADIUS_SQ) continue;
                    const cx = ox + dx * step, cy = oy + dy * step, cz = oz + dz * step;
                    if (collision.querySphere(cx, cy, cz, radius, scratchPush)) continue;
                    bestDistSq = distSq;
                    out.x = cx; out.y = cy; out.z = cz;
                    found = true;
                }
            }
        }
    }
    return found;
};

/** Find the closest standable cylinder placement; out is the floor world position. */
const findCylinderSpawn = (
    collision: Collision,
    ox: number, oy: number, oz: number,
    halfHeight: number,
    radius: number,
    out: SpawnOut
): boolean => {
    const step = collision.voxelResolution;
    const maxCells = Math.ceil(SEARCH_RADIUS / step);
    const footCells = Math.ceil(radius / step);
    const radiusSq = radius * radius;
    let bestDistSq = Infinity;
    let found = false;

    for (let r = 0; r <= maxCells; r++) {
        const shellMinDistSq = (r * step) * (r * step);
        if (shellMinDistSq >= bestDistSq) break;
        for (let dy = -r; dy <= r; dy++) {
            const absDy = dy < 0 ? -dy : dy;
            for (let dz = -r; dz <= r; dz++) {
                const absDz = dz < 0 ? -dz : dz;
                for (let dx = -r; dx <= r; dx++) {
                    const absDx = dx < 0 ? -dx : dx;
                    if (absDx < r && absDy < r && absDz < r) continue;
                    const distSq = (dx * dx + dy * dy + dz * dz) * step * step;
                    if (distSq >= bestDistSq || distSq > SEARCH_RADIUS_SQ) continue;
                    const cx = ox + dx * step, cy = oy + dy * step, cz = oz + dz * step;
                    if (!collision.isFreeAt(cx, cy, cz)) continue;

                    let floor = -Infinity, ceiling = Infinity, supported = true;
                    for (let i = -footCells; i <= footCells && supported; i++) {
                        const fxOff = i * step, fxOffSq = fxOff * fxOff;
                        for (let j = -footCells; j <= footCells; j++) {
                            const fzOff = j * step;
                            if (fxOffSq + fzOff * fzOff > radiusSq) continue;
                            const fx = cx + fxOff, fz = cz + fzOff;
                            const down = collision.queryRay(fx, cy, fz, 0, -1, 0, RAY_MAX_DIST);
                            if (!down) { supported = false; break; }
                            if (down.y > floor) floor = down.y;
                            const up = collision.queryRay(fx, cy, fz, 0, 1, 0, RAY_MAX_DIST);
                            if (up && up.y < ceiling) ceiling = up.y;
                        }
                    }
                    if (!supported) continue;
                    if (floor + 2 * halfHeight > ceiling) continue;
                    bestDistSq = distSq;
                    out.x = cx; out.y = floor; out.z = cz;
                    found = true;
                }
            }
        }
    }
    return found;
};

export { findCylinderSpawn, findSphereSpawn };
